import type { AnimationConfig } from './types';

/**
 * postMessage contract between the Operator Console (parent) and the iframe's
 * `/animation-preview` route (child). These strings cross a window boundary,
 * so they are part of the public surface and must stay in step on both sides.
 */
export const PREVIEW_MESSAGES = {
  /** Parent → child: what to show. */
  APPLY: 'animation-preview-apply',
  /** Parent → child: play (autoSlice), shuffle (the carousel's ring) or reset the running scene. */
  COMMAND: 'animation-preview-command',
  /** Child → parent: the listener is up, please (re)send APPLY. */
  READY: 'animation-preview-ready',
  /** Child → parent: the scene could not run (no WebGL, artwork refused, …). */
  ERROR: 'animation-preview-error',
} as const;

export const PREVIEW_TEMPLATES = ['classic', 'modern', 'sleek', 'glass'] as const;
export type PreviewTemplate = (typeof PREVIEW_TEMPLATES)[number];

export function isPreviewTemplate(value: unknown): value is PreviewTemplate {
  return PREVIEW_TEMPLATES.some((name) => name === value);
}

/**
 * Rarity badges the preview can pretend to pull, top tier first — the names
 * the backend sends on a real card and the reveal's `glowColorFor` reads.
 */
export const PREVIEW_RARITIES: readonly string[] = [
  'Grail',
  'Chase',
  'Epic',
  'Rare',
  'Uncommon',
  'Common',
  'Big Hit',
];

export interface PreviewApplyMessage {
  type: typeof PREVIEW_MESSAGES.APPLY;
  /** Raw section — the child normalises it with `parseAnimation`. */
  animation: AnimationConfig;
  /** Full design_system v2, so the child paints the operator's palette. */
  designSystem: Record<string, unknown>;
  template: PreviewTemplate;
  packImageUrl: string;
  /** Real cards of the pack; the child pulls a different one each cycle. */
  cardImageUrls?: string[];
  /** Badge of the pretend pull (one of PREVIEW_RARITIES); absent = a different one each cycle. */
  rarity?: string;
}

export type PreviewCommand = 'play' | 'reset' | 'shuffle';

export interface PreviewCommandMessage {
  type: typeof PREVIEW_MESSAGES.COMMAND;
  command: PreviewCommand;
}

export interface PreviewReadyMessage {
  type: typeof PREVIEW_MESSAGES.READY;
}

export interface PreviewErrorMessage {
  type: typeof PREVIEW_MESSAGES.ERROR;
  message: string;
}

type Plain = Record<string, unknown>;

const isPlain = (value: unknown): value is Plain =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

export function isPreviewApplyMessage(data: unknown): data is PreviewApplyMessage {
  return (
    isPlain(data) &&
    data['type'] === PREVIEW_MESSAGES.APPLY &&
    isPlain(data['animation']) &&
    isPlain(data['designSystem']) &&
    isPreviewTemplate(data['template']) &&
    typeof data['packImageUrl'] === 'string' &&
    (data['cardImageUrls'] === undefined || isStringArray(data['cardImageUrls'])) &&
    (data['rarity'] === undefined || typeof data['rarity'] === 'string')
  );
}

export function isPreviewCommandMessage(data: unknown): data is PreviewCommandMessage {
  return (
    isPlain(data) &&
    data['type'] === PREVIEW_MESSAGES.COMMAND &&
    (data['command'] === 'play' || data['command'] === 'reset' || data['command'] === 'shuffle')
  );
}

export function isPreviewReadyMessage(data: unknown): data is PreviewReadyMessage {
  return isPlain(data) && data['type'] === PREVIEW_MESSAGES.READY;
}

export function isPreviewErrorMessage(data: unknown): data is PreviewErrorMessage {
  return (
    isPlain(data) && data['type'] === PREVIEW_MESSAGES.ERROR && typeof data['message'] === 'string'
  );
}
